/**
 * Position des Melden-Knopfs: Er lässt sich verschieben, weil er sonst Inhalte
 * verdecken kann. Gemerkt je Browser (localStorage) als Abstand zum jeweils
 * NÄCHSTEN Rand (je Achse links/rechts bzw. oben/unten); ohne gespeicherten
 * Wert steht er per CSS unten rechts.
 *
 * Warum Anker statt linker oberer Ecke (T-273): Eine absolute Ecke wurde beim
 * Verkleinern des Fensters (Edge-Seitenleiste, Zoom, Andocken) ins Fenster
 * geklemmt und ÜBERSCHRIEB dabei die gewünschte Position — wurde das Fenster
 * wieder groß, stand der Knopf mitten im Bild. Der Anker ist die Wunschposition,
 * geklemmt wird erst beim Anzeigen (`posAus`).
 */
export const KNOPF_GROESSE = 64;
/** Ab so vielen Pixeln Bewegung ist es ein Ziehen, kein Klick. */
export const ZIEH_SCHWELLE = 5;
const RAND = 8;
const SCHLUESSEL = "kmvs.ticket.melden-knopf";

export type KnopfPos = { x: number; y: number };
export type KnopfAnker = { ankerX: "links" | "rechts"; dx: number; ankerY: "oben" | "unten"; dy: number };

/** Hält den Knopf vollständig im sichtbaren Fenster. */
export function begrenze(p: KnopfPos, breite: number, hoehe: number, groesse = KNOPF_GROESSE): KnopfPos {
  const maxX = Math.max(RAND, breite - groesse - RAND);
  const maxY = Math.max(RAND, hoehe - groesse - RAND);
  return {
    x: Math.round(Math.min(Math.max(p.x, RAND), maxX)),
    y: Math.round(Math.min(Math.max(p.y, RAND), maxY)),
  };
}

/** Absolute linke obere Ecke → Abstand zum nächsten Rand je Achse. */
export function ankerAus(p: KnopfPos, breite: number, hoehe: number, groesse = KNOPF_GROESSE): KnopfAnker {
  const links = p.x + groesse / 2 < breite / 2;
  const oben = p.y + groesse / 2 < hoehe / 2;
  return {
    ankerX: links ? "links" : "rechts",
    dx: Math.max(0, Math.round(links ? p.x : breite - p.x - groesse)),
    ankerY: oben ? "oben" : "unten",
    dy: Math.max(0, Math.round(oben ? p.y : hoehe - p.y - groesse)),
  };
}

/** Anker → absolute, ins aktuelle Fenster geklemmte linke obere Ecke. */
export function posAus(a: KnopfAnker, breite: number, hoehe: number, groesse = KNOPF_GROESSE): KnopfPos {
  const x = a.ankerX === "links" ? a.dx : breite - a.dx - groesse;
  const y = a.ankerY === "oben" ? a.dy : hoehe - a.dy - groesse;
  return begrenze({ x, y }, breite, hoehe, groesse);
}

const zahlOk = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

/**
 * Liest den gespeicherten Anker. Das Altformat `{x, y}` (linke obere Ecke)
 * wird mit der aktuellen Fenstergröße umgerechnet, damit verschobene Knöpfe
 * nach dem Update nicht springen.
 */
export function ladePos(breite: number, hoehe: number): KnopfAnker | null {
  try {
    const roh = JSON.parse(localStorage.getItem(SCHLUESSEL) ?? "null") as unknown;
    if (roh && typeof roh === "object") {
      const r = roh as Record<string, unknown>;
      if ("ankerX" in r || "ankerY" in r) {
        if ((r.ankerX === "links" || r.ankerX === "rechts") && (r.ankerY === "oben" || r.ankerY === "unten")
          && zahlOk(r.dx) && zahlOk(r.dy) && r.dx >= 0 && r.dy >= 0) {
          return { ankerX: r.ankerX, dx: r.dx, ankerY: r.ankerY, dy: r.dy };
        }
        return null;
      }
      if (zahlOk(r.x) && zahlOk(r.y)) return ankerAus(begrenze({ x: r.x, y: r.y }, breite, hoehe), breite, hoehe);
    }
  } catch {
    /* gesperrt/kaputt — dann eben Standardplatz */
  }
  return null;
}

export function speicherePos(a: KnopfAnker | null): void {
  try {
    if (a) localStorage.setItem(SCHLUESSEL, JSON.stringify(a));
    else localStorage.removeItem(SCHLUESSEL);
  } catch {
    /* best effort */
  }
}
